import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { HapticNotificationFeedbackStyle, triggerHapticNotificationFeedback } from '@sg/garnish';
import type {
  AddLineItemToCartInput,
  DeliveryOrderDetailInput,
  IngredientModificationInput,
  IngredientSubstitutionModificationInput,
  MixedDressingDetails,
} from '@sg/graphql-schema';
import { useCartMutationErrorNotice } from '@sg/shared-fe-features-product-details';

import { addCartLineItemToSharedLineItemAttribution } from '@order/shared-line-item';
import { useTelemetry } from '@order/Telemetry';
import { getUrqlError } from '@order/utils';

import { useSnapPixel } from '../../../integrations/snap-pixel/hooks/useSnapPixel';
import { useDeliveryOrderInFlight } from '../../useDeliveryOrderInFlight';
import { useNetworkErrorNotice } from '../../useNetworkErrorNotice';
import { useAddLineItemToCartMutation } from '../GraphQL/Cart.generated';
import { useCart } from '../useCart';
import { extractIngredientId, extractSubstitution, getLineItemCustomName } from '../useCart.utils';
import { useLogCartChangeCustomEvent } from '../useLogCartChangeCustomEvent';

// ────────────────────────────────────────────────────────────────────────────────

export const useAddLineItemToCart = (props?: UseAddLineItemToCartParams) => {
  const { onSuccess, onError } = props ?? {};
  const { cart, executeCartMutation } = useCart();
  const { formatMessage } = useIntl();
  const logCartChangeCustomEvent = useLogCartChangeCustomEvent();
  const { trackAddCart: trackSnapPixelAddCart } = useSnapPixel();

  const [addLineItemResponse, addLineItem] = useAddLineItemToCartMutation();
  const { data, fetching } = addLineItemResponse;

  useCartMutationErrorNotice(data?.addLineItemToCart);
  useNetworkErrorNotice(addLineItemResponse);

  // ─── In Flight Delivery Orders ───────────────────────────────────

  const { inFlightDeliveryOrderId, navigateToDeliveryOrderInFlightScreen } =
    useDeliveryOrderInFlight({ requestPolicy: 'cache-only' });

  // ─── TELEMETRY ───────────────────────────────────────────────────

  const { track } = useTelemetry();

  const onAddToBagFailedTelemetry = useCallback(
    (params: OnAddToBagFailedTelemetryParams) => {
      const { sharedLineItemSlug, systemError, userError } = params;

      track('add_to_bag.failed', {
        userError: userError ?? formatMessage(messages.addLineItemError),
        systemError,
        ...(sharedLineItemSlug ? { isSharedLineItem: true, sharedLineItemSlug } : {}),
      });
    },
    [formatMessage, track],
  );

  // ─── HELPERS ─────────────────────────────────────────────────────

  const addLineItemToCart = useCallback(
    async (inputProps: AddItemToCartInputProps) => {
      // Block adding delivery product when there is an in-flight delivery order.
      if (inputProps.deliveryDetails?.addressId && inFlightDeliveryOrderId) {
        navigateToDeliveryOrderInFlightScreen();

        return;
      }

      const {
        product,
        quantity = 1,
        modifications,
        deliveryDetails,
        customName = null,
        totalCost,
        sharedLineItemSlug,
      } = inputProps;

      const productCost = totalCost ?? product.cost;
      const previousCartLineItemIds = cart?.lineItems.map(({ id }) => id) ?? [];

      const { id: productId = '', name: productName = '' } = product;

      const {
        additions = [],
        removals = [],
        substitutions = [],
        mixedDressingDetails = [],
      } = modifications ?? {};

      const deliveryOrderDetails = {
        addressId: deliveryDetails?.addressId ?? '',
        vendor: deliveryDetails?.vendor ?? '',
        vendorRestaurantId: deliveryDetails?.vendorRestaurantId ?? '',
        deliveryFee: deliveryDetails?.deliveryFee ?? 0,
        tip: deliveryDetails?.tip ?? 0,
      };

      const input: AddLineItemToCartInput = {
        productId,
        customName: getLineItemCustomName({ productName, customName }),
        quantity,
        mixedDressingDetails,
        additions: additions.map(extractIngredientId),
        removals: removals.map(extractIngredientId),
        substitutions: substitutions.map(extractSubstitution),
        ...(deliveryOrderDetails?.addressId ? { deliveryOrderDetails } : {}),
      };

      const executeMutation = async () => addLineItem({ input });
      const response = await executeCartMutation(executeMutation, 'add-line-item');
      const result = response.data?.addLineItemToCart;
      const possibleError = result as Readonly<{ message?: string }>;
      const isSuccessfullyAdded = result?.__typename === 'AddLineItemToCartSuccess';

      if (!isSuccessfullyAdded) {
        onAddToBagFailedTelemetry({
          sharedLineItemSlug,
          systemError: getUrqlError(response.error),
          userError: possibleError?.message,
        });

        onError?.({
          message: possibleError?.message,
          product,
        });

        return;
      }

      const updatedCart = result.cart;

      const addedCartLineItemId = getAddedCartLineItemId({
        cart: updatedCart,
        previousCartLineItemIds,
        productId,
      });

      if (sharedLineItemSlug && addedCartLineItemId) {
        addCartLineItemToSharedLineItemAttribution({
          sharedLineItemSlug,
          cartLineItemId: addedCartLineItemId,
        });
      }

      logCartChangeCustomEvent({ action: 'ADD', cart: updatedCart, productName });

      trackSnapPixelAddCart({
        priceInDollars: productCost ? productCost / 100 : undefined,
        productId,
        productName,
        quantity,
      });

      void triggerHapticNotificationFeedback(HapticNotificationFeedbackStyle.Success);
      onSuccess?.({
        product,
        quantity,
      });
    },
    [
      inFlightDeliveryOrderId,
      executeCartMutation,
      logCartChangeCustomEvent,
      trackSnapPixelAddCart,
      onSuccess,
      navigateToDeliveryOrderInFlightScreen,
      addLineItem,
      onAddToBagFailedTelemetry,
      onError,
      cart?.lineItems,
    ],
  );

  // ─────────────────────────────────────────────────────────────────

  return useMemo(() => ({ addLineItemToCart, fetching }), [addLineItemToCart, fetching]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getAddedCartLineItemId(params: GetAddedCartLineItemIdParams) {
  const { cart, previousCartLineItemIds, productId } = params;

  return cart.lineItems?.find((lineItem) => {
    const hasExistingLineItem = previousCartLineItemIds.includes(lineItem.id);
    const hasMatchingProduct = lineItem.product?.id === productId;

    return !hasExistingLineItem && hasMatchingProduct;
  })?.id;
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  addLineItemError: {
    defaultMessage: 'Something went wrong, try again in a few moments...',
    description: 'Add Line Item To Cart | Error',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type UseAddLineItemToCartParams = Readonly<{
  onSuccess?: ({ product, quantity }: { product: PartialProduct; quantity: number }) => void;
  onError?: ({ product, message }: { product: PartialProduct; message?: string }) => void;
}>;

type AddItemToCartInputProps = Readonly<{
  product: PartialProduct;
  customName?: string | null;
  quantity?: number;
  totalCost?: number;
  deliveryDetails?: DeliveryOrderDetailInput;
  sharedLineItemSlug?: string;
  modifications?: {
    additions: readonly IngredientModificationInput[];
    removals: readonly IngredientModificationInput[];
    substitutions: readonly IngredientSubstitutionModificationInput[];
    mixedDressingDetails: readonly MixedDressingDetails[];
  };
}>;

type PartialProduct = { id: string; name: string; cost?: number };

type OnAddToBagFailedTelemetryParams = {
  sharedLineItemSlug?: string;
  systemError?: string;
  userError?: string;
};

type GetAddedCartLineItemIdParams = {
  cart: AddLineItemCart;
  previousCartLineItemIds: readonly string[];
  productId: string;
};

type AddLineItemCart = {
  lineItems?: ReadonlyArray<{
    id: string;
    product?: { id: string };
  }>;
};
